import { chaveDosGuids } from './clashReconciliacao'
import { sanitizeTriage, temParecer, type ClashTriageEntry, type ClashTriageMap } from './clashTriage'
import type { ClashGrupoGravado } from './clashAgrupamentoEstado'

// ---------------------------------------------------------------------------
// Triagem partilhada no workspace: o modelo, puro.
//
// IDENTIDADE. A chave local de um conflito (`modelo:localId|modelo:localId|
// tipo`) usa os localId do Fragments, que nao sao os mesmos entre browsers
// nem entre revisoes. No servidor, um conflito e o par de GlobalIds
// ordenado e o tipo (`chaveDosGuids`), que e o mesmo para quem abrir o
// mesmo IFC. A ponte entre as duas e o GlobalId guardado na entrada da
// triagem ou o resolvido dos resultados da corrida.
//
// CAMPO A CAMPO. Cada entrada passa a uma lista de campos normalizados
// (vazio e "por omissao" sao null, para "nao ha entrada" e "entrada
// vazia" serem a mesma coisa). O que se envia e so o campo que mudou, e o
// servidor funde campo a campo: duas pessoas a mexer em campos diferentes
// do mesmo conflito nunca se pisam. No mesmo campo, ganha a ultima
// escrita (pelo momento da alteracao), com aviso.
//
// O QUE NAO SE PARTILHA. A ultima geometria conhecida (`ultimo`, em
// coordenadas da cena de quem correu), os GlobalIds (sao a identidade) e
// as coisas de cada browser: as chaves da ultima corrida, o envio para o
// modulo BCF e o registo da ultima exportacao para ficheiro.
//
// Os dados que vem do servidor sao lidos com os mesmos sanitizadores com
// que se le um ficheiro: o servidor so valida a forma.
// ---------------------------------------------------------------------------

export type TipoItem = 'conflito' | 'grupo' | 'config' | 'corrida'

/** Quem mudou um campo, quando e em que versao. */
export interface Carimbo {
  v: number
  em: string
  por: string
  nome: string
}

/** Um item como o servidor o tem. */
export interface ItemPartilhado {
  tipo: TipoItem
  chave: string
  campos: Record<string, unknown>
  carimbos: Record<string, Carimbo>
  versao: number
}

/** O que o servidor tem, como este browser o leu: por `idDoItem`. */
export type Espelho = Record<string, ItemPartilhado>

/** Um campo alterado aqui e ainda por confirmar pelo servidor. */
export interface CampoPendente {
  valor: unknown
  /** Momento da alteracao (ISO). */
  em: string
}

/** A fila de envio: o ultimo valor de cada campo alterado, por item. */
export type Fila = Record<string, { tipo: TipoItem; chave: string; campos: Record<string, CampoPendente> }>

/** Uma alteracao como vai para a API. */
export interface AlteracaoParaEnvio {
  tipo: TipoItem
  chave: string
  campos: Record<string, unknown>
  em: string
}

/** Aviso de conflito de campo (ver backend/modules/clash/regras.py). */
export interface AvisoDoServidor {
  tipo: string
  chave: string
  campo: string
  outro_id: string
  outro_nome: string
  ficou: 'enviado' | 'anterior'
  segundos: number
}

export const CAMPOS_CONFLITO = [
  'state',
  'note',
  'label',
  'priority',
  'assignedTo',
  'notify',
  'recommendation',
  'dueDate',
  'tags',
  'group',
  'groupTitle',
  'eventos',
  'porVerificar',
  'origem',
  'regra',
  'tipoConflito',
  'nivel',
  'grupoFixo',
  'foraDaExclusao',
] as const

export const CAMPOS_GRUPO = ['titulo', 'descricao', 'identidade', 'metodo', 'prioridade', 'manual'] as const

export const CAMPOS_CLASSIFICACAO = [
  'regras',
  'especialidades',
  'agrupamento',
  'exclusoes',
  'classificadoEm',
  'agrupadoEm',
] as const

export const CAMPOS_SETUP = [
  'sideA',
  'sideB',
  'preset',
  'tolerance',
  'clearance',
  'ignoreContacts',
  'selfClash',
  'penetracaoMinima',
  'folgaPares',
] as const

/** Itens de configuracao (chave do item config). */
export const CONFIG_CLASSIFICACAO = 'classificacao'
export const CONFIG_SETUP = 'setup'
export const CONFIG_PROJECTO = 'projecto'

/** Nomes dos campos para os avisos. */
export const NOMES_DOS_CAMPOS: Record<string, string> = {
  state: 'o estado',
  note: 'a nota',
  priority: 'a prioridade',
  assignedTo: 'o responsável',
  notify: 'a quem comunicar',
  recommendation: 'a recomendação',
  dueDate: 'o prazo',
  tags: 'as etiquetas',
  group: 'o grupo',
  groupTitle: 'o título do grupo',
  titulo: 'o título do grupo',
  descricao: 'a descrição do grupo',
  regras: 'as regras de classificação',
  agrupamento: 'o método de agrupamento',
  exclusoes: 'as regras de exclusão',
  foraDaExclusao: 'a reabertura da regra de exclusão',
  penetracaoMinima: 'a penetração mínima',
  folgaPares: 'os pares da folga',
  tolerance: 'a tolerância',
  clearance: 'a folga',
  sideA: 'o conjunto A',
  sideB: 'o conjunto B',
}

export function idDoItem(tipo: TipoItem, chave: string): string {
  return `${tipo}:${chave}`
}

/** O tipo do conflito (hard ou clearance) da chave local. */
export function tipoDaChave(key: string): string {
  return key.slice(key.lastIndexOf('|') + 1)
}

/** A identidade partilhada de um conflito: pelo GlobalId guardado na
 *  entrada ou pelo resolvido da corrida (`chaves`). */
export function identidadeDaChave(
  key: string,
  entry: ClashTriageEntry | undefined,
  chaves: Readonly<Record<string, string>>,
): string | null {
  if (entry?.guids && entry.guids[0] && entry.guids[1]) {
    return chaveDosGuids(entry.guids, tipoDaChave(key))
  }
  return chaves[key] ?? null
}

/** Os dois GlobalIds de uma identidade. */
export function guidsDaIdentidade(id: string): [string, string] | null {
  const p = id.split('|')
  return p.length === 3 && p[0] && p[1] ? [p[0], p[1]] : null
}

// --- Normalizacao e comparacao -----------------------------------------------

/** Vazio, falso e "por omissao" passam a null: "nao ha" e "esta vazio"
 *  sao o mesmo para a fusao. */
export function normalizar(campo: string, v: unknown): unknown {
  if (v === undefined || v === null || v === false) return null
  if (typeof v === 'string' && v.trim() === '') return null
  if (Array.isArray(v) && v.length === 0) return null
  if (campo === 'state' && v === 'novo') return null
  return v
}

function estavel(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(estavel)
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>
    return Object.fromEntries(
      Object.keys(o)
        .sort()
        .filter((k) => o[k] !== undefined)
        .map((k) => [k, estavel(o[k])]),
    )
  }
  return v
}

/** Igualdade de valores JSON, sem contar a ordem das chaves. */
export function iguais(a: unknown, b: unknown): boolean {
  if (a === b) return true
  return JSON.stringify(estavel(a ?? null)) === JSON.stringify(estavel(b ?? null))
}

/** Os campos partilhados de uma entrada (ou de nenhuma). */
export function camposDaEntrada(e: ClashTriageEntry | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  const o = (e ?? {}) as unknown as Record<string, unknown>
  for (const c of CAMPOS_CONFLITO) out[c] = normalizar(c, o[c])
  return out
}

/**
 * A entrada da triagem que os campos descrevem, ou undefined se nao
 * dizem nada (a entrada sai). Passa pelo `sanitizeTriage`: o que vem do
 * servidor e lido como se le um ficheiro. A geometria de `base` fica.
 */
export function entradaDosCampos(
  key: string,
  campos: Record<string, unknown>,
  base: ClashTriageEntry | undefined,
  guids: [string, string] | null,
  updatedAt: string,
): ClashTriageEntry | undefined {
  const bruto: Record<string, unknown> = {
    state: campos.state ?? 'novo',
    note: campos.note ?? '',
    updatedAt,
  }
  for (const c of CAMPOS_CONFLITO) {
    if (c === 'state' || c === 'note') continue
    const v = campos[c]
    if (v !== null && v !== undefined) bruto[c] = v
  }
  const g = guids ?? base?.guids ?? null
  if (g) bruto.guids = g
  const lida = Object.values(sanitizeTriage({ [key]: bruto }))[0]
  if (!lida) return undefined
  if (base?.ultimo) lida.ultimo = base.ultimo
  return temParecer(lida) ? lida : undefined
}

/** Os campos de um item com o que esta por enviar por cima: o que esta
 *  na fila ganha no ecra ate o servidor decidir. */
export function camposEfectivos(
  item: ItemPartilhado | undefined,
  pendente: Fila[string] | undefined,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...(item?.campos ?? {}) }
  for (const [c, p] of Object.entries(pendente?.campos ?? {})) out[c] = p.valor
  return out
}

/** Momento mais recente dos carimbos de um item (para o updatedAt). */
export function ultimoCarimbo(item: ItemPartilhado | undefined): string | null {
  let out: string | null = null
  for (const c of Object.values(item?.carimbos ?? {})) {
    if (c && typeof c.em === 'string' && (out === null || c.em > out)) out = c.em
  }
  return out
}

// --- Indice identidade <-> chave local ---------------------------------------

/** Para cada identidade, as chaves locais que lhe correspondem. */
export function indiceDeIdentidades(
  triagem: ClashTriageMap,
  chaves: Readonly<Record<string, string>>,
): Map<string, string[]> {
  const out = new Map<string, string[]>()
  const juntar = (id: string, k: string) => {
    const l = out.get(id)
    if (!l) out.set(id, [k])
    else if (!l.includes(k)) l.push(k)
  }
  for (const [k, e] of Object.entries(triagem)) {
    const id = identidadeDaChave(k, e, chaves)
    if (id) juntar(id, k)
  }
  for (const [k, id] of Object.entries(chaves)) juntar(id, k)
  return out
}

/** Das entradas de uma identidade, a que conta: com decisao primeiro,
 *  depois a mais recente. */
function melhor(triagem: ClashTriageMap, keys: readonly string[]): ClashTriageEntry | undefined {
  let out: ClashTriageEntry | undefined
  for (const k of keys) {
    const e = triagem[k]
    if (!e) continue
    if (!out) {
      out = e
      continue
    }
    const pe = temParecer(e)
    const po = temParecer(out)
    if ((pe && !po) || (pe === po && e.updatedAt > out.updatedAt)) out = e
  }
  return out
}

// --- Diferencas locais -> alteracoes -------------------------------------------

export interface DiferencasDaTriagem {
  alteracoes: AlteracaoParaEnvio[]
  /** Chaves alteradas sem identidade (sem GlobalId conhecido ainda). */
  semIdentidade: string[]
}

/**
 * O que mudou na triagem entre duas fotografias, por identidade e campo
 * a campo. Uma entrada que mudou de chave (outra revisao do IFC, mesma
 * identidade) nao e alteracao. `apagar`: as entradas que desapareceram
 * contam como apagadas (todos os campos a null); falso nas substituicoes
 * e nas reconciliacoes, que nunca apagam.
 */
export function diferencasDaTriagem(
  antes: ClashTriageMap,
  depois: ClashTriageMap,
  chaves: Readonly<Record<string, string>>,
  agora: string,
  apagar = true,
): DiferencasDaTriagem {
  const mudadas = new Set<string>()
  for (const k of Object.keys(depois)) if (antes[k] !== depois[k]) mudadas.add(k)
  for (const k of Object.keys(antes)) if (!(k in depois)) mudadas.add(k)
  if (mudadas.size === 0) return { alteracoes: [], semIdentidade: [] }

  const semIdentidade: string[] = []
  const tocadas = new Set<string>()
  for (const k of mudadas) {
    const id = identidadeDaChave(k, depois[k] ?? antes[k], chaves)
    if (id) tocadas.add(id)
    else semIdentidade.push(k)
  }
  const indAntes = indiceDeIdentidades(antes, chaves)
  const indDepois = indiceDeIdentidades(depois, chaves)
  const alteracoes: AlteracaoParaEnvio[] = []
  for (const id of tocadas) {
    const ea = melhor(antes, indAntes.get(id) ?? [])
    const ed = melhor(depois, indDepois.get(id) ?? [])
    if (!ed && !apagar) continue
    const ca = camposDaEntrada(ea)
    const cd = camposDaEntrada(ed)
    const campos: Record<string, unknown> = {}
    for (const c of CAMPOS_CONFLITO) if (!iguais(ca[c], cd[c])) campos[c] = cd[c]
    if (Object.keys(campos).length === 0) continue
    const em = ed && ed.updatedAt && !Number.isNaN(Date.parse(ed.updatedAt)) ? ed.updatedAt : agora
    alteracoes.push({ tipo: 'conflito', chave: id, campos, em })
  }
  return { alteracoes, semIdentidade }
}

function camposDoGrupo(g: ClashGrupoGravado | undefined): Record<string, unknown> {
  const o = (g ?? {}) as unknown as Record<string, unknown>
  const out: Record<string, unknown> = {}
  for (const c of CAMPOS_GRUPO) out[c] = normalizar(c, o[c])
  return out
}

/** Os grupos que mudaram. Um grupo que desaparece vai com tudo a null
 *  (quando `apagar`). */
export function diferencasDosGrupos(
  antes: Readonly<Record<string, ClashGrupoGravado>>,
  depois: Readonly<Record<string, ClashGrupoGravado>>,
  em: string,
  apagar = true,
): AlteracaoParaEnvio[] {
  if (antes === depois) return []
  const out: AlteracaoParaEnvio[] = []
  for (const id of new Set([...Object.keys(antes), ...Object.keys(depois)])) {
    if (antes[id] === depois[id]) continue
    if (!depois[id] && !apagar) continue
    const ca = camposDoGrupo(antes[id])
    const cd = camposDoGrupo(depois[id])
    const campos: Record<string, unknown> = {}
    for (const c of CAMPOS_GRUPO) if (!iguais(ca[c], cd[c])) campos[c] = cd[c]
    if (Object.keys(campos).length > 0) out.push({ tipo: 'grupo', chave: id, campos, em })
  }
  return out
}

/** Os campos de um item de configuracao que mudaram. */
export function diferencasDoObjecto(
  chave: string,
  campos: readonly string[],
  antes: Readonly<Record<string, unknown>>,
  depois: Readonly<Record<string, unknown>>,
  em: string,
): AlteracaoParaEnvio[] {
  const mud: Record<string, unknown> = {}
  for (const c of campos) {
    const a = normalizar(c, antes[c])
    const d = normalizar(c, depois[c])
    if (!iguais(a, d)) mud[c] = d
  }
  return Object.keys(mud).length > 0 ? [{ tipo: 'config', chave, campos: mud, em }] : []
}

/** As corridas novas (pela data). */
export function corridasNovas<T extends { data: string }>(
  antes: readonly T[],
  depois: readonly T[],
  em: string,
): AlteracaoParaEnvio[] {
  if (antes === depois) return []
  const vistas = new Set(antes.map((c) => c.data))
  return depois
    .filter((c) => !vistas.has(c.data))
    .map((c) => ({ tipo: 'corrida' as const, chave: c.data, campos: { corrida: c }, em }))
}

// --- Fila --------------------------------------------------------------------

/** Junta alteracoes a fila (o valor mais recente de cada campo fica). */
export function juntarAFila(fila: Fila, alteracoes: readonly AlteracaoParaEnvio[]): Fila {
  if (alteracoes.length === 0) return fila
  const out: Fila = { ...fila }
  for (const a of alteracoes) {
    const id = idDoItem(a.tipo, a.chave)
    const actual = out[id]
    const campos = { ...(actual?.campos ?? {}) }
    for (const [c, v] of Object.entries(a.campos)) campos[c] = { valor: v, em: a.em }
    out[id] = { tipo: a.tipo, chave: a.chave, campos }
  }
  return out
}

/** A fila em alteracoes para a API: uma por item e por momento. */
export function filaParaEnvio(fila: Fila): AlteracaoParaEnvio[] {
  const out: AlteracaoParaEnvio[] = []
  for (const it of Object.values(fila)) {
    const porMomento = new Map<string, Record<string, unknown>>()
    for (const [c, p] of Object.entries(it.campos)) {
      const m = porMomento.get(p.em) ?? {}
      m[c] = p.valor
      porMomento.set(p.em, m)
    }
    for (const [em, campos] of porMomento) out.push({ tipo: it.tipo, chave: it.chave, campos, em })
  }
  return out
}

/** Tira da fila o que foi enviado, se nao mudou entretanto (um campo
 *  alterado outra vez durante o envio fica para o envio seguinte). */
export function tirarDaFila(fila: Fila, enviadas: readonly AlteracaoParaEnvio[]): Fila {
  const out: Fila = { ...fila }
  for (const a of enviadas) {
    const id = idDoItem(a.tipo, a.chave)
    const it = out[id]
    if (!it) continue
    const campos = { ...it.campos }
    for (const [c, v] of Object.entries(a.campos)) {
      const p = campos[c]
      if (p && p.em === a.em && iguais(p.valor, v)) delete campos[c]
    }
    if (Object.keys(campos).length === 0) delete out[id]
    else out[id] = { ...it, campos }
  }
  return out
}

/** Tira da fila um item inteiro (alteracao recusada pela forma). */
export function tirarItemDaFila(fila: Fila, tipo: string, chave: string): Fila {
  const id = `${tipo}:${chave}`
  if (!(id in fila)) return fila
  const out = { ...fila }
  delete out[id]
  return out
}

/** Itens por enviar (para o "alteracoes por enviar (N)"). */
export function pendentesNaFila(fila: Fila): number {
  return Object.keys(fila).length
}

// --- Leitura do que vem do servidor ------------------------------------------

/** Le um item da API. Item estragado fica de fora. */
export function lerItem(v: unknown): ItemPartilhado | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  const tipo = o.tipo
  if (tipo !== 'conflito' && tipo !== 'grupo' && tipo !== 'config' && tipo !== 'corrida') return null
  if (typeof o.chave !== 'string' || !o.chave) return null
  const campos = o.campos && typeof o.campos === 'object' && !Array.isArray(o.campos) ? (o.campos as Record<string, unknown>) : {}
  const carimbos: Record<string, Carimbo> = {}
  const cb = o.carimbos && typeof o.carimbos === 'object' ? (o.carimbos as Record<string, unknown>) : {}
  for (const [c, x] of Object.entries(cb)) {
    const k = x as Record<string, unknown> | null
    if (!k || typeof k !== 'object') continue
    carimbos[c] = {
      v: typeof k.v === 'number' ? k.v : 0,
      em: typeof k.em === 'string' ? k.em : '',
      por: typeof k.por === 'string' ? k.por : '',
      nome: typeof k.nome === 'string' ? k.nome : '',
    }
  }
  return { tipo, chave: o.chave, campos, carimbos, versao: typeof o.versao === 'number' ? o.versao : 0 }
}

/** Le um aviso da API ou do canal. */
export function lerAviso(v: unknown): AvisoDoServidor | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  if (typeof o.campo !== 'string' || typeof o.chave !== 'string') return null
  return {
    tipo: typeof o.tipo === 'string' ? o.tipo : 'conflito',
    chave: o.chave,
    campo: o.campo,
    outro_id: typeof o.outro_id === 'string' ? o.outro_id : '',
    outro_nome: typeof o.outro_nome === 'string' && o.outro_nome ? o.outro_nome : 'outra pessoa',
    ficou: o.ficou === 'anterior' ? 'anterior' : 'enviado',
    segundos: typeof o.segundos === 'number' ? o.segundos : 0,
  }
}

/** Junta itens lidos ao espelho (o item do servidor e o estado inteiro). */
export function juntarAoEspelho(espelho: Espelho, itens: readonly ItemPartilhado[]): Espelho {
  if (itens.length === 0) return espelho
  const out = { ...espelho }
  for (const it of itens) out[idDoItem(it.tipo, it.chave)] = it
  return out
}

// --- Textos ------------------------------------------------------------------

function segundosTexto(s: number): string {
  const n = Math.max(1, Math.round(s))
  return n < 60 ? `${n} s` : `${Math.round(n / 60)} min`
}

/**
 * O aviso para quem ENVIOU (resposta da API): "ficou o seu" ou "ficou o
 * do colega". `rotulo` descreve o item com a preposicao ("do conflito
 * IFCBEAM x IFCCOLUMN", ver rotuloDoItem).
 */
export function textoDoAvisoEnviado(a: AvisoDoServidor, rotulo: string): string {
  const campo = NOMES_DOS_CAMPOS[a.campo] ?? `o campo ${a.campo}`
  if (a.ficou === 'anterior') {
    return `${a.outro_nome} mudou ${campo} ${rotulo} depois da sua alteração: ficou o valor de ${a.outro_nome}.`
  }
  return `${a.outro_nome} também mudou ${campo} ${rotulo} (${segundosTexto(a.segundos)} de diferença): ficou o seu valor.`
}

/** O aviso para quem PERDEU o valor (pelo canal). */
export function textoDoAvisoRecebido(a: AvisoDoServidor, quem: string, rotulo: string): string {
  const campo = NOMES_DOS_CAMPOS[a.campo] ?? `o campo ${a.campo}`
  return `${quem} mudou ${campo} ${rotulo} logo a seguir a si (${segundosTexto(a.segundos)} de diferença): ficou o valor de ${quem}.`
}

export type EstadoDaLigacao = 'ao-vivo' | 'a-sincronizar' | 'sem-canal' | 'sem-ligacao' | 'parada'

/** A linha de estado da barra da partilha. */
export function textoDoEstado(o: {
  estado: EstadoDaLigacao
  pessoas: number
  pendentes: number
}): string {
  const pessoas = `${o.pessoas} pessoa${o.pessoas === 1 ? '' : 's'}`
  switch (o.estado) {
    case 'ao-vivo':
      return o.pendentes > 0
        ? `Partilhada · ao vivo · ${pessoas} · a enviar (${o.pendentes})`
        : `Partilhada · ao vivo · ${pessoas}`
    case 'a-sincronizar':
      return 'Partilhada · a sincronizar…'
    case 'sem-canal':
      return o.pendentes > 0
        ? `Partilhada · sem canal ao vivo · alterações por enviar (${o.pendentes})`
        : 'Partilhada · sem canal ao vivo (atualiza de 20 em 20 s)'
    case 'sem-ligacao':
      return `Sem ligação · alterações por enviar (${o.pendentes})`
    default:
      return 'Partilha parada'
  }
}

/** O conflito de uma identidade, para os avisos: o rotulo da entrada
 *  local, se houver, ou o do servidor. */
export function rotuloDoItem(
  tipo: string,
  chave: string,
  espelho: Espelho,
  triagem: ClashTriageMap,
  indice: Map<string, string[]>,
): string {
  if (tipo === 'grupo') return `do grupo ${String(espelho[idDoItem('grupo', chave)]?.campos.titulo ?? chave)}`
  if (tipo === 'config') return 'da configuração'
  if (tipo !== 'conflito') return 'da triagem'
  for (const k of indice.get(chave) ?? []) {
    const l = triagem[k]?.label
    if (l) return `do conflito ${l}`
  }
  const l = espelho[idDoItem('conflito', chave)]?.campos.label
  return typeof l === 'string' && l ? `do conflito ${l}` : 'de um conflito'
}
